/**
 * Mino Keys — Infinite Scroll + Sort Handler
 *
 * Owns sorting entirely — intercepts the SortBy dropdown before Impulse
 * can handle it, fetches sorted results ourselves, replaces the grid,
 * and reinitializes the scroll observer. This means sort + infinite scroll
 * always stay in sync regardless of how Impulse handles AJAX internally.
 */
(function () {
  'use strict';

  // ── STATE ──────────────────────────────────────────────────────────────────
  let sectionId      = null;
  let scrollObserver = null;
  let isFetching     = false;
  let isOurWrite     = false; // suppresses any MutationObserver side-effects

  // ── BOOTSTRAP ──────────────────────────────────────────────────────────────
  function getRoot()       { return document.getElementById('CollectionInfiniteRoot'); }
  function getGrid()       { const r = getRoot(); return r ? r.querySelector('[data-product-grid]') : null; }
  function getPagination() { const r = getRoot(); return r ? r.querySelector('[data-pagination]') : null; }

  // ── NEXT PAGE URL ──────────────────────────────────────────────────────────
  // Works with Impulse's pagination which has no rel="next" attribute.
  // Finds the link whose ?page= param is exactly currentPage + 1.
  // Sort/filter params come along for free because Shopify Liquid already
  // includes them in every pagination href.
  function getNextUrl() {
    const pagination = getPagination();
    if (!pagination) return null;

    // Standard rel="next" (other themes)
    const relNext = pagination.querySelector('a[rel="next"]');
    if (relNext) return relNext.href;

    // Impulse fallback — find current page number, look for page N+1 link
    const currentSpan = pagination.querySelector('.page.current, .current, [aria-current="page"]');
    if (!currentSpan) return null;

    const currentPage = parseInt(currentSpan.textContent.trim(), 10);
    if (isNaN(currentPage)) return null;

    const nextPage = currentPage + 1;
    const nextLink = Array.from(pagination.querySelectorAll('a[href]')).find(function (a) {
      try {
        return parseInt(new URL(a.href, window.location.origin).searchParams.get('page'), 10) === nextPage;
      } catch (e) { return false; }
    });

    return nextLink ? nextLink.href : null;
  }

  // ── FETCH HELPER ───────────────────────────────────────────────────────────
  async function fetchCollection(pageUrl) {
    const url = new URL(pageUrl, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    const response = await fetch(url.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error('[Mino Keys] Fetch failed: ' + response.status);
    return response.text();
  }

  function parseHTML(html) {
    const doc  = new DOMParser().parseFromString(html, 'text/html');
    const root = doc.getElementById('CollectionInfiniteRoot');
    if (!root) return null;
    return {
      items:      Array.from((root.querySelector('[data-product-grid]') || { children: [] }).children),
      pagination: root.querySelector('[data-pagination]')
    };
  }

  // ── IMAGE LOADER ───────────────────────────────────────────────────────────
  // Uses setAttribute('src') instead of img.src = so protocol-relative URLs
  // resolve against the live page base (not DOMParser's about:blank).
  function prepareImages(items) {
    items.forEach(function (item) {

      item.querySelectorAll('img[data-src]').forEach(function (img) {
        const dataSrc = img.getAttribute('data-src');
        if (!dataSrc) return;
        img.classList.remove('lazyload');
        img.classList.add('lazyloaded');
        img.setAttribute('src', dataSrc);
        const widthsAttr = img.getAttribute('data-widths');
        if (widthsAttr) {
          try {
            const widths = JSON.parse(widthsAttr);
            const srcset = widths.map(function (w) {
              return dataSrc.replace(/([?&]width=)\d+/, '$1' + w) + ' ' + w + 'w';
            }).join(', ');
            img.setAttribute('srcset', srcset);
            img.setAttribute('sizes', '(max-width: 767px) 50vw, 25vw');
          } catch (e) {}
        }
        img.removeAttribute('data-src');
        img.removeAttribute('data-widths');
        img.removeAttribute('data-sizes');
        img.removeAttribute('data-aspectratio');
      });

      item.querySelectorAll('[data-bgset]').forEach(function (el) {
        const bgset = el.getAttribute('data-bgset');
        if (!bgset) return;
        let imageUrl = bgset.split(',')[0].trim().split(' ')[0];
        if (imageUrl.indexOf('//') === 0) imageUrl = window.location.protocol + imageUrl;
        el.style.backgroundImage    = 'url("' + imageUrl + '")';
        el.style.backgroundSize     = 'cover';
        el.style.backgroundPosition = 'center center';
        el.style.backgroundRepeat   = 'no-repeat';
        el.classList.remove('lazyload');
        el.classList.add('lazyloaded');
        el.removeAttribute('data-bgset');
      });
    });
  }

  // ── GRID REPLACE ───────────────────────────────────────────────────────────
  // Called after a sort change — clears the grid and fills it with new items.
  function replaceGrid(items, paginationEl) {
    const grid       = getGrid();
    const pagination = getPagination();
    if (!grid) return;

    isOurWrite = true;

    // Swap images before inserting
    prepareImages(items);

    grid.innerHTML = '';
    items.forEach(function (item) {
      item.classList.add('aos-animate');
      grid.appendChild(item);
    });

    if (pagination) {
      pagination.innerHTML = paginationEl ? paginationEl.innerHTML : '';
    }

    isOurWrite = false;

    if (window.AOS) AOS.refresh();
  }

  // ── GRID APPEND ────────────────────────────────────────────────────────────
  // Called by infinite scroll — appends the next page's items to the grid.
  function appendToGrid(items, paginationEl) {
    const grid       = getGrid();
    const pagination = getPagination();
    if (!grid) return;

    prepareImages(items);

    isOurWrite = true;
    items.forEach(function (item) {
      item.classList.add('aos-animate');
      grid.appendChild(item);
    });
    if (pagination) {
      pagination.innerHTML = paginationEl ? paginationEl.innerHTML : '';
    }
    isOurWrite = false;

    if (window.AOS) AOS.refresh();
  }

  // ── SCROLL OBSERVER ────────────────────────────────────────────────────────
  function cleanupObserver() {
    if (scrollObserver) { scrollObserver.disconnect(); scrollObserver = null; }
    const s = document.getElementById('CollectionInfiniteSentinel');
    if (s) s.remove();
  }

  function initScrollObserver() {
    cleanupObserver();

    const root = getRoot();
    if (!root) return;
    if (!getNextUrl()) return; // already on last page

    const sentinel = document.createElement('div');
    sentinel.id = 'CollectionInfiniteSentinel';
    sentinel.style.height = '1px';
    sentinel.setAttribute('aria-hidden', 'true');
    root.appendChild(sentinel);

    scrollObserver = new IntersectionObserver(async function (entries) {
      if (!entries[0].isIntersecting || isFetching) return;

      const nextUrl = getNextUrl();
      if (!nextUrl) {
        cleanupObserver();
        return;
      }

      isFetching = true;

      try {
        const html   = await fetchCollection(nextUrl);
        const parsed = parseHTML(html);
        if (!parsed) throw new Error('[Mino Keys] Could not parse next page');

        appendToGrid(parsed.items, parsed.pagination);

        if (!getNextUrl()) cleanupObserver();

      } catch (err) {
        console.error(err);
        cleanupObserver();
      } finally {
        isFetching = false;
      }

    }, { rootMargin: '400px 0px' });

    scrollObserver.observe(sentinel);
  }

  // ── SORT HANDLER ───────────────────────────────────────────────────────────
  // Intercepts the SortBy dropdown in the capture phase (fires before Impulse).
  // Fetches sorted page 1 ourselves, replaces the grid, reinits scroll.
  async function handleSort(sortValue) {
    if (isFetching) return;
    isFetching = true;

    const grid = getGrid();
    if (!grid) { isFetching = false; return; }

    // Build the sorted URL from the current page (preserves any active filters)
    const sortedUrl = new URL(window.location.href);
    sortedUrl.searchParams.set('sort_by', sortValue);
    sortedUrl.searchParams.delete('page'); // always fetch page 1 of the new sort

    // Update browser URL so the address bar reflects the sort
    history.pushState({ sort_by: sortValue }, '', sortedUrl.toString());

    // Visual feedback — dim grid while loading
    grid.style.opacity        = '0.35';
    grid.style.pointerEvents  = 'none';
    grid.style.transition     = 'opacity 0.2s';

    try {
      const html   = await fetchCollection(sortedUrl.toString());
      const parsed = parseHTML(html);
      if (!parsed) throw new Error('[Mino Keys] Sort response had no grid');

      replaceGrid(parsed.items, parsed.pagination);

      // Reinitialize scroll observer for the newly sorted content
      initScrollObserver();

      // Smooth scroll back to top of collection
      const root = getRoot();
      if (root) root.scrollIntoView({ behavior: 'smooth', block: 'start' });

    } catch (err) {
      console.error(err);
      // On failure, fall back to a full page navigation
      window.location.href = sortedUrl.toString();
    } finally {
      if (grid) {
        grid.style.opacity       = '';
        grid.style.pointerEvents = '';
        grid.style.transition    = '';
      }
      isFetching = false;
    }
  }

  function initSortHandling() {
    const sortSelect = document.getElementById('SortBy');
    if (!sortSelect) return;

    // Capture phase fires before Impulse's own listener.
    // stopImmediatePropagation() prevents Impulse from also running its AJAX.
    sortSelect.addEventListener('change', function (e) {
      e.stopImmediatePropagation();
      handleSort(this.value);
    }, true);
  }

  // ── BROWSER BACK / FORWARD ─────────────────────────────────────────────────
  window.addEventListener('popstate', function () {
    const sortValue  = new URL(window.location.href).searchParams.get('sort_by') || '';
    const sortSelect = document.getElementById('SortBy');
    if (sortSelect) sortSelect.value = sortValue;
    handleSort(sortValue);
  });

  // ── INIT ───────────────────────────────────────────────────────────────────
  function init() {
    const root = getRoot();
    if (!root) return;

    sectionId = root.getAttribute('data-section-id');
    if (!sectionId) return;

    initScrollObserver();
    initSortHandling();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

}());